/**
 * TrafficMetrics.tsx
 *
 * KPI row + new/returning split + daily line chart for the traffic-audience
 * metrics endpoint, over a caller-supplied date range. Fetching goes through
 * useAPI()'s requestTrafficAnalytics — assumed to take (startDate, endDate)
 * and return an axios response whose .data matches TrafficAudienceMetrics.
 * Styling (.stat-tile, .kpi-row, .split-bar-block, .status-row, etc.) is
 * defined once in the parent AnalyticsDashboard's <style> block.
 */
import { useEffect, useRef, useState } from 'react';
import useAPI from '../../Hooks/useAPI';

export interface TrafficAudienceMetrics {
    totalPageviews: string;
    uniqueVisitors: string;
    newVisitors: string;
    returningVisitors: string;
    avgSessionsPerVisitor: string;
}

export interface DailyVisitorPoint {
    date: Date;
    uniqueVisitors: number;
}

function previousEquivalentRange(start: Date, end: Date): { start: Date; end: Date } {
    const rangeMs = end.getTime() - start.getTime();
    return { start: new Date(start.getTime() - rangeMs), end: new Date(start.getTime()) };
}

// ---------------------------------------------------------------------
// Data fetch — via useAPI()'s requestTrafficAnalytics
// ---------------------------------------------------------------------
function useTrafficAudience(start: Date, end: Date) {
    const { requestTrafficAnalytics } = useAPI();

    const [current, setCurrent] = useState<TrafficAudienceMetrics | null>(null);
    const [previous, setPrevious] = useState<TrafficAudienceMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        const prevRange = previousEquivalentRange(start, end);

        Promise.all([
            requestTrafficAnalytics(start.toISOString(), end.toISOString()),
            requestTrafficAnalytics(prevRange.start.toISOString(), prevRange.end.toISOString()),
        ])
            .then(([currentResponse, previousResponse]) => {
                if (cancelled) return;
                setCurrent(currentResponse.data);
                setPrevious(previousResponse.data);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Failed to load traffic/audience metrics:', err);
                setError(err instanceof Error ? err.message : 'Failed to load metrics.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [start.getTime(), end.getTime(), requestTrafficAnalytics]);

    return { current, previous, loading, error };
}

// The traffic-audience endpoint only returns one aggregate per call, so a
// daily breakdown means one call per day in the range. Uncapped, a wide
// custom range (a year, say) would fire hundreds of requests at once — cap
// how many days we ask for and fetch them a handful at a time.
const MAX_DAILY_POINTS = 92;
const DAILY_FETCH_CONCURRENCY = 8;

function getDaysInRange(start: Date, end: Date): Date[] {
    const days: Date[] = [];
    const cursor = new Date(start);
    cursor.setHours(0, 0, 0, 0);
    const last = new Date(end);
    last.setHours(0, 0, 0, 0);
    while (cursor.getTime() <= last.getTime()) {
        days.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
    }
    return days;
}

function useDailyUniqueVisitors(start: Date, end: Date) {
    const { requestTrafficAnalytics } = useAPI();

    const [points, setPoints] = useState<DailyVisitorPoint[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [truncated, setTruncated] = useState<{ shown: number; total: number } | null>(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        const allDays = getDaysInRange(start, end);
        const days = allDays.length > MAX_DAILY_POINTS ? allDays.slice(-MAX_DAILY_POINTS) : allDays;

        async function fetchDay(day: Date): Promise<DailyVisitorPoint> {
            const dayEnd = new Date(day);
            dayEnd.setHours(23, 59, 59, 999);
            const response = await requestTrafficAnalytics(day.toISOString(), dayEnd.toISOString());
            return { date: day, uniqueVisitors: Number(response.data?.uniqueVisitors) || 0 };
        }

        // Simple bounded worker pool — fetch several days at a time rather
        // than all at once or strictly one at a time.
        async function fetchAll(): Promise<DailyVisitorPoint[]> {
            const results: DailyVisitorPoint[] = new Array(days.length);
            let next = 0;
            async function worker() {
                while (next < days.length) {
                    const i = next++;
                    results[i] = await fetchDay(days[i]);
                }
            }
            const workers = Array.from({ length: Math.min(DAILY_FETCH_CONCURRENCY, days.length) }, worker);
            await Promise.all(workers);
            return results;
        }

        fetchAll()
            .then((results) => {
                if (cancelled) return;
                setPoints(results);
                setTruncated(allDays.length > days.length ? { shown: days.length, total: allDays.length } : null);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Failed to load daily unique visitors:', err);
                setError(err instanceof Error ? err.message : 'Failed to load daily visitors.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [start.getTime(), end.getTime(), requestTrafficAnalytics]);

    return { points, loading, error, truncated };
}

// ---------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------
function formatCompact(value: string): string {
    const n = Number(value);
    if (Number.isNaN(n)) return value;
    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

function formatDelta(currentVal: string, previousVal: string): { text: string; direction: 'up' | 'down' | 'flat' } {
    const cur = Number(currentVal);
    const prev = Number(previousVal);
    if (Number.isNaN(cur) || Number.isNaN(prev) || prev === 0) {
        return { text: '—', direction: 'flat' };
    }
    const pct = ((cur - prev) / prev) * 100;
    if (Math.abs(pct) < 0.5) return { text: '±0%', direction: 'flat' };
    const direction = pct > 0 ? 'up' : 'down';
    return { text: `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`, direction };
}

function formatDateLabel(d: Date, opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' }): string {
    return d.toLocaleDateString('en-US', opts);
}

// "Nice" round number >= value, for y-axis ticks that read as clean numbers
// (0 / 1,000 / 2,000) rather than whatever the max data point happens to be.
function niceMax(value: number): number {
    if (value <= 0) return 1;
    const exponent = Math.floor(Math.log10(value));
    const fraction = value / Math.pow(10, exponent);
    const niceFraction = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 5 ? 5 : 10;
    return niceFraction * Math.pow(10, exponent);
}

// ---------------------------------------------------------------------
// Stat tile
// ---------------------------------------------------------------------
function StatTile({
    label,
    value,
    delta,
}: {
    label: string;
    value: string;
    delta: { text: string; direction: 'up' | 'down' | 'flat' } | null;
}) {
    return (
        <div className="stat-tile">
            <div className="stat-tile-label">{label}</div>
            <div className="stat-tile-value">{formatCompact(value)}</div>
            {delta && (
                <div className={`stat-tile-delta stat-tile-delta--${delta.direction}`} title="vs. previous period">
                    {delta.text}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// New vs Returning donut (part-to-whole, 2 categories)
// ---------------------------------------------------------------------
function NewReturningPie({ newVisitors, returningVisitors }: { newVisitors: string; returningVisitors: string }) {
    const n = Number(newVisitors) || 0;
    const r = Number(returningVisitors) || 0;
    const total = n + r;
    const newPct = total > 0 ? (n / total) * 100 : 0;
    const returningPct = total > 0 ? (r / total) * 100 : 0;

    const cx = 50;
    const cy = 50;
    const radius = 34;
    const strokeWidth = 16;
    const circumference = 2 * Math.PI * radius;
    // Surface-color gap between segments — the donut's version of the stacked
    // bar's 2px spacer, so touching segments still read as distinct.
    const gap = 3;

    const newLen = Math.max((newPct / 100) * circumference - gap, 0);
    const returningLen = Math.max((returningPct / 100) * circumference - gap, 0);

    return (
        <div className="split-bar-block">
            <div className="split-bar-heading">New vs. returning visitors</div>
            <div className="donut-chart-row">
                <div className="donut-chart-wrap">
                    <svg viewBox="0 0 100 100" width="100%" height="100%" role="img" aria-label="New vs returning visitor split">
                        {/* rotate so the first segment starts at 12 o'clock */}
                        <g transform={`rotate(-90 ${cx} ${cy})`}>
                            <circle
                                cx={cx}
                                cy={cy}
                                r={radius}
                                fill="none"
                                strokeWidth={strokeWidth}
                                strokeLinecap="round"
                                strokeDasharray={`${newLen} ${circumference - newLen}`}
                                strokeDashoffset={0}
                                className="donut-segment donut-segment--new"
                            >
                                <title>{`New: ${n.toLocaleString()} (${newPct.toFixed(1)}%)`}</title>
                            </circle>
                            <circle
                                cx={cx}
                                cy={cy}
                                r={radius}
                                fill="none"
                                strokeWidth={strokeWidth}
                                strokeLinecap="round"
                                strokeDasharray={`${returningLen} ${circumference - returningLen}`}
                                strokeDashoffset={-(newLen + gap)}
                                className="donut-segment donut-segment--returning"
                            >
                                <title>{`Returning: ${r.toLocaleString()} (${returningPct.toFixed(1)}%)`}</title>
                            </circle>
                        </g>
                    </svg>
                    <div className="donut-center-label">
                        <div className="donut-center-value">{formatCompact(String(total))}</div>
                        <div className="donut-center-caption">Visitors</div>
                    </div>
                </div>
                <div className="split-bar-legend split-bar-legend--vertical">
                    <span className="split-bar-legend-item">
                        <span className="split-bar-swatch split-bar-swatch--new" />
                        New — {n.toLocaleString()} ({newPct.toFixed(1)}%)
                    </span>
                    <span className="split-bar-legend-item">
                        <span className="split-bar-swatch split-bar-swatch--returning" />
                        Returning — {r.toLocaleString()} ({returningPct.toFixed(1)}%)
                    </span>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------
// Unique visitors over time (single-series line chart, one point per day)
// ---------------------------------------------------------------------
function UniqueVisitorsLineChart({ points }: { points: DailyVisitorPoint[] }) {
    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const svgRef = useRef<SVGSVGElement>(null);

    const width = 600;
    const height = 220;
    const padding = { top: 16, right: 16, bottom: 28, left: 40 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;

    const n = points.length;
    const maxValue = niceMax(Math.max(...points.map((p) => p.uniqueVisitors), 1));

    const xFor = (i: number) => padding.left + (n <= 1 ? plotWidth / 2 : (i / (n - 1)) * plotWidth);
    const yFor = (v: number) => padding.top + plotHeight - (v / maxValue) * plotHeight;

    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)},${yFor(p.uniqueVisitors)}`).join(' ');
    const areaPath = n > 0 ? `${linePath} L ${xFor(n - 1)},${padding.top + plotHeight} L ${xFor(0)},${padding.top + plotHeight} Z` : '';

    const yTicks = [0, maxValue / 2, maxValue];
    const hovered = hoverIndex !== null ? points[hoverIndex] : undefined;
    const last = points[n - 1];

    const updateHoverFromClientX = (clientX: number) => {
        const svg = svgRef.current;
        if (!svg || n === 0) return;
        const rect = svg.getBoundingClientRect();
        const localX = (clientX - rect.left) * (width / rect.width);
        const ratio = n <= 1 ? 0 : (localX - padding.left) / plotWidth;
        setHoverIndex(Math.min(Math.max(Math.round(ratio * (n - 1)), 0), n - 1));
    };

    return (
        <div className="split-bar-block line-chart-block">
            <div className="split-bar-heading">Unique visitors over time</div>
            <div className="line-chart-wrap">
                <svg
                    ref={svgRef}
                    viewBox={`0 0 ${width} ${height}`}
                    width="100%"
                    height="100%"
                    role="img"
                    aria-label="Unique visitors by day"
                    tabIndex={0}
                    onPointerMove={(e) => updateHoverFromClientX(e.clientX)}
                    onPointerLeave={() => setHoverIndex(null)}
                    onFocus={() => setHoverIndex((i) => (i === null ? n - 1 : i))}
                    onBlur={() => setHoverIndex(null)}
                    onKeyDown={(e) => {
                        if (e.key === 'ArrowLeft') {
                            e.preventDefault();
                            setHoverIndex((i) => Math.max((i ?? n) - 1, 0));
                        } else if (e.key === 'ArrowRight') {
                            e.preventDefault();
                            setHoverIndex((i) => Math.min((i ?? -1) + 1, n - 1));
                        }
                    }}
                >
                    {yTicks.map((t, i) => (
                        <line key={i} x1={padding.left} x2={width - padding.right} y1={yFor(t)} y2={yFor(t)} className="line-chart-gridline" />
                    ))}

                    {areaPath && <path d={areaPath} className="line-chart-area" />}
                    {linePath && <path d={linePath} className="line-chart-line" fill="none" />}

                    {hovered && hoverIndex !== null && (
                        <>
                            <line
                                x1={xFor(hoverIndex)}
                                x2={xFor(hoverIndex)}
                                y1={padding.top}
                                y2={padding.top + plotHeight}
                                className="line-chart-crosshair"
                            />
                            <circle cx={xFor(hoverIndex)} cy={yFor(hovered.uniqueVisitors)} r={4} className="line-chart-dot" />
                        </>
                    )}

                    {yTicks.map((t, i) => (
                        <text
                            key={i}
                            x={padding.left - 8}
                            y={yFor(t)}
                            className="line-chart-axis-label"
                            textAnchor="end"
                            dominantBaseline="middle"
                        >
                            {formatCompact(String(Math.round(t)))}
                        </text>
                    ))}

                    {n > 0 && (
                        <>
                            <text x={xFor(0)} y={height - 8} className="line-chart-axis-label" textAnchor="start">
                                {formatDateLabel(points[0].date)}
                            </text>
                            {n > 1 && (
                                <text x={xFor(n - 1)} y={height - 8} className="line-chart-axis-label" textAnchor="end">
                                    {formatDateLabel(points[n - 1].date)}
                                </text>
                            )}
                        </>
                    )}

                    {last && (
                        <text x={xFor(n - 1)} y={yFor(last.uniqueVisitors) - 8} className="line-chart-endpoint-label" textAnchor="end">
                            {last.uniqueVisitors.toLocaleString()}
                        </text>
                    )}
                </svg>

                {hovered && hoverIndex !== null && (
                    <div
                        className="line-chart-tooltip"
                        style={{ left: `${(xFor(hoverIndex) / width) * 100}%`, top: `${(yFor(hovered.uniqueVisitors) / height) * 100}%` }}
                    >
                        <div className="line-chart-tooltip-value">{hovered.uniqueVisitors.toLocaleString()}</div>
                        <div className="line-chart-tooltip-date">
                            {formatDateLabel(hovered.date, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>
                    </div>
                )}
            </div>

            {/* Every value shown above (hover, endpoint label) is also reachable
                here without hovering — the table is the non-interactive fallback. */}
            <details className="line-chart-table-toggle">
                <summary>View as table</summary>
                <div className="line-chart-table-wrap">
                    <table className="line-chart-table">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Unique visitors</th>
                            </tr>
                        </thead>
                        <tbody>
                            {points.map((p) => (
                                <tr key={p.date.toISOString()}>
                                    <td>{formatDateLabel(p.date, { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                                    <td>{p.uniqueVisitors.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </details>
        </div>
    );
}

// ---------------------------------------------------------------------
// Section — kpi row + charts row, gated on the current-period fetch
// ---------------------------------------------------------------------
export default function TrafficMetricsSection({ start, end }: { start: Date; end: Date }) {
    const { current, previous, loading, error } = useTrafficAudience(start, end);
    const { points: dailyPoints, loading: dailyLoading, error: dailyError, truncated: dailyTruncated } = useDailyUniqueVisitors(start, end);

    return (
        <>
            {loading && <div className="status-row">Loading…</div>}
            {error && <div className="status-row status-row--error">{error}</div>}

            {current && (
                <>
                    <div className="kpi-row">
                        <StatTile
                            label="Total pageviews"
                            value={current.totalPageviews}
                            delta={previous ? formatDelta(current.totalPageviews, previous.totalPageviews) : null}
                        />
                        <StatTile
                            label="Unique visitors"
                            value={current.uniqueVisitors}
                            delta={previous ? formatDelta(current.uniqueVisitors, previous.uniqueVisitors) : null}
                        />
                        <StatTile
                            label="New visitors"
                            value={current.newVisitors}
                            delta={previous ? formatDelta(current.newVisitors, previous.newVisitors) : null}
                        />
                        <StatTile
                            label="Returning visitors"
                            value={current.returningVisitors}
                            delta={previous ? formatDelta(current.returningVisitors, previous.returningVisitors) : null}
                        />
                        <StatTile
                            label="Avg. sessions / visitor"
                            value={current.avgSessionsPerVisitor}
                            delta={previous ? formatDelta(current.avgSessionsPerVisitor, previous.avgSessionsPerVisitor) : null}
                        />
                    </div>

                    <div className="charts-row">
                        <div className="line-chart-column">
                            {dailyTruncated && (
                                <div className="status-row">
                                    Showing the most recent {dailyTruncated.shown} of {dailyTruncated.total} days — narrow the range to see every day.
                                </div>
                            )}
                            {dailyError && <div className="status-row status-row--error">{dailyError}</div>}
                            {dailyLoading && dailyPoints.length === 0 && <div className="status-row">Loading…</div>}
                            {dailyPoints.length > 0 && <UniqueVisitorsLineChart points={dailyPoints} />}
                        </div>

                        <NewReturningPie newVisitors={current.newVisitors} returningVisitors={current.returningVisitors} />
                    </div>
                </>
            )}
        </>
    );
}
